import React, { useState, useRef, useEffect } from 'react';
import './CameraCapture.css';

const CameraCapture = ({ onCapture, onClose }) => {
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    startCamera();
    return () => {
      // Cleanup function - ensure camera is stopped when component unmounts
      if (stream) {
        stream.getTracks().forEach(track => {
          track.stop();
          console.log('Camera track stopped:', track.kind);
        });
        setStream(null);
      }
      // Also clean up video element
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, []);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment',
          width: { ideal: 800 }, // Reduced from 1280
          height: { ideal: 600 }  // Reduced from 720
        } 
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Unable to access camera. Please check permissions.');
    }
  };

  // Compress image to fit within Firestore limits
  const compressImage = (canvas, maxSizeBytes = 500000) => { // 500KB limit
    let quality = 0.7;
    let dataUrl = canvas.toDataURL('image/jpeg', quality);
    
    // If still too large, reduce quality further
    while (dataUrl.length > maxSizeBytes && quality > 0.1) {
      quality -= 0.1;
      dataUrl = canvas.toDataURL('image/jpeg', quality);
    }
    
    // If still too large, reduce canvas size
    if (dataUrl.length > maxSizeBytes) {
      const scale = Math.sqrt(maxSizeBytes / dataUrl.length) * 0.8; // 20% buffer
      const newWidth = Math.floor(canvas.width * scale);
      const newHeight = Math.floor(canvas.height * scale);
      
      const tempCanvas = document.createElement('canvas');
      const tempContext = tempCanvas.getContext('2d');
      tempCanvas.width = newWidth;
      tempCanvas.height = newHeight;
      
      tempContext.drawImage(canvas, 0, 0, newWidth, newHeight);
      dataUrl = tempCanvas.toDataURL('image/jpeg', 0.5);
    }
    
    return dataUrl;
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      // Set canvas size to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw video frame to canvas
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Compress the image to fit within Firestore limits
      const compressedImageData = compressImage(canvas);
      setCapturedImage(compressedImageData);
      
      // Stop the camera stream immediately after capturing
      if (stream) {
        stream.getTracks().forEach(track => {
          track.stop();
          console.log('Camera track stopped after capture:', track.kind);
        });
        setStream(null);
      }
      // Clean up video element
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    // Restart camera for retake
    startCamera();
  };

  const savePhoto = () => {
    if (capturedImage) {
      // Stop the camera stream before closing
      if (stream) {
        stream.getTracks().forEach(track => {
          track.stop();
          console.log('Camera track stopped on save:', track.kind);
        });
        setStream(null);
      }
      // Clean up video element
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      onCapture(capturedImage);
      onClose();
    }
  };

  const handleClose = () => {
    // Always stop the camera stream when closing
    if (stream) {
      stream.getTracks().forEach(track => {
        track.stop();
        console.log('Camera track stopped on close:', track.kind);
      });
      setStream(null);
    }
    // Clean up video element
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    onClose();
  };

  return (
    <div className="camera-capture-container">
      <div className="camera-capture-content">
        <div className="camera-header">
          <h3>Take Photo</h3>
          <button className="close-button" onClick={handleClose}>&times;</button>
        </div>
        
        <div className="camera-body">
          {!capturedImage ? (
            <div className="camera-preview">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="camera-video"
              />
              <div className="camera-controls">
                <button 
                  className="capture-button"
                  onClick={capturePhoto}
                >
                  Take Photo
                </button>
              </div>
            </div>
          ) : (
            <div className="photo-preview">
              <img 
                src={capturedImage} 
                alt="Captured" 
                className="captured-image"
              />
              <div className="photo-controls">
                <button 
                  className="retake-button"
                  onClick={retakePhoto}
                >
                  Retake
                </button>
                <button 
                  className="save-button"
                  onClick={savePhoto}
                >
                  Save Photo
                </button>
              </div>
            </div>
          )}
        </div>
        
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
    </div>
  );
};

export default CameraCapture; 